/**
 * RenewalAlertsWidget — Bandeau des contrats à reconduction tacite dont
 * la fin approche (défaut 90j) avec 1-clic pour rédiger la lettre
 * d'opposition à la reconduction.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bell, Calendar, AlertTriangle, ArrowRight, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import LetterAssistantDialog from "@/components/LetterAssistantDialog";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR",
  { day: "2-digit", month: "short", year: "numeric" }) : "—";

const URG_META = {
  critical: { cls: "bg-red-100 text-red-800 border-red-300", label: "Critique" },
  high: { cls: "bg-orange-100 text-orange-800 border-orange-300", label: "Urgent" },
  medium: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "À suivre" },
  low: { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Prévoir" },
};

export default function RenewalAlertsWidget({ compact = false }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showLetter, setShowLetter] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contracts/renewal-alerts?days=90");
        setAlerts(data);
      } catch (e) { setAlerts({ alerts: [] }); }
    })();
  }, []);

  const openLetter = (alert) => {
    setSelectedContract({
      id: alert.id, title: alert.title, party_name: alert.party_name,
    });
    setShowLetter(true);
  };

  if (!alerts) {
    return (
      <Card className="p-4 flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Chargement des alertes…
      </Card>
    );
  }
  if (!alerts.alerts?.length) return null;

  const critical = alerts.alerts.filter(a => a.urgency === "critical").length;
  const high = alerts.alerts.filter(a => a.urgency === "high").length;

  return (
    <>
      <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50/50"
            data-testid="renewal-alerts-widget">
        <div className="p-4">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
                <Bell className="h-4 w-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  Reconductions tacites imminentes · {alerts.count}
                </div>
                <div className="text-[11px] text-slate-600">
                  {critical > 0 && <span className="text-red-700 font-medium">{critical} critique{critical > 1 ? "s" : ""} · </span>}
                  {high > 0 && <span className="text-orange-700">{high} urgent{high > 1 ? "s" : ""} · </span>}
                  Fenêtre 90 jours
                </div>
              </div>
            </div>
          </div>

          <div className={cn("space-y-1.5 overflow-y-auto",
                              compact ? "max-h-56" : "max-h-96")}>
            {alerts.alerts.slice(0, compact ? 5 : 20).map((a) => (
              <div key={a.id}
                   className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2.5 hover:border-amber-400 transition"
                   data-testid={`renewal-alert-${a.id}`}>
                <Badge className={cn("text-[10px] shrink-0", URG_META[a.urgency].cls)}>
                  J-{a.days_remaining}
                </Badge>
                <button onClick={() => navigate(`/app/contracts/${a.id}`)}
                        className="flex-1 min-w-0 text-left group">
                  <div className="text-xs font-semibold text-slate-900 group-hover:text-blue-700 truncate">
                    {a.title || a.party_name}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{a.party_name}</span>
                    <span>·</span>
                    <span>Fin {fmtDate(a.end_date)}</span>
                    <span>·</span>
                    <span>Reconduction +{a.renewal_period_months || 12} mois</span>
                    {a.notice_overdue && (
                      <Badge className="bg-red-100 text-red-800 border-red-300 text-[9px] py-0 px-1">
                        <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Préavis dépassé
                      </Badge>
                    )}
                  </div>
                </button>
                <Button size="sm" onClick={() => openLetter(a)}
                        className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs shrink-0"
                        data-testid={`renewal-letter-${a.id}`}>
                  <FileText className="h-3 w-3 mr-1" /> Lettre
                </Button>
              </div>
            ))}
          </div>

          {alerts.alerts.length > (compact ? 5 : 20) && (
            <div className="mt-2 text-center">
              <button onClick={() => navigate("/app/contracts/echeances")}
                      className="text-xs text-amber-700 hover:underline font-medium inline-flex items-center gap-1">
                Voir toutes ({alerts.count}) <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </Card>

      {selectedContract && (
        <LetterAssistantDialog
          open={showLetter}
          onOpenChange={(v) => { setShowLetter(v); if (!v) setSelectedContract(null); }}
          contract={selectedContract}
        />
      )}
    </>
  );
}
